import React from 'react';
import { Box, Text, Flex } from '@chakra-ui/react';
import { formatCurrency } from 'utils/CommonFunction';
import PropTypes from 'prop-types';

export const CustomTooltipForGrossRevenue = ({
  active,
  payload,
  currency,
  showAverage,
  grossRevenueAvg,
  globalMerchantColors,
}) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;

    return (
      <div
        className="custom-tooltip"
        style={{
          background: '#fff',
          padding: '10px',
          border: '1px solid #ccc',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          maxWidth: '300px',
        }}
      >
        {/* Average */}
        {showAverage && (
          <Flex alignItems="center" mb="2">
            <Box w="10px" h="10px" bg="red" borderRadius="50%" mr="2" />
            <Text fontSize="sm">
              Average: {formatCurrency(grossRevenueAvg, currency)}
            </Text>
          </Flex>
        )}

        {/* Conditional content based on merchant data */}
        {data.merchants &&
        Array.isArray(data.merchants) &&
        data.merchants.length > 0 ? (
          // Show merchant details when merchants exist
          <>
            <Text fontWeight="bold" mb="2" fontSize="sm">
              {data.time}
            </Text>
            {(() => {
              // Filter out merchants with zero values and sort by total (descending)
              const activeMerchants = data.merchants
                .filter((merchant) => Number(merchant.total) > 0)
                .sort((a, b) => Number(b.total) - Number(a.total));

              const maxDisplay = 8; // Show max 8 merchants
              const displayMerchants = activeMerchants.slice(0, maxDisplay);
              const remainingCount = activeMerchants.length - maxDisplay;

              return (
                <>
                  {/* Scrollable container for merchant list */}
                  <Box maxH="200px" overflowY="auto" mb="2">
                    {displayMerchants.map((merchant) => {
                      const color =
                        globalMerchantColors[merchant.merchantId] || '#666';

                      return (
                        <Flex
                          key={merchant.merchantId}
                          alignItems="center"
                          mb="1"
                        >
                          <Box
                            w="8px"
                            h="8px"
                            bg={color}
                            borderRadius="50%"
                            mr="2"
                          />
                          <Text fontSize="xs" fontWeight="medium" flex="1">
                            {merchant.name.length > 15
                              ? `${merchant.name.substring(0, 15)}...`
                              : merchant.name}
                            :{' '}
                            <Text as="span" fontWeight="bold">
                              {formatCurrency(merchant.total, currency)}
                            </Text>
                          </Text>
                        </Flex>
                      );
                    })}
                  </Box>

                  {/* Summary for remaining merchants */}
                  {remainingCount > 0 && (
                    <Text fontSize="xs" color="gray.500" textAlign="center">
                      +{remainingCount} more merchants
                    </Text>
                  )}

                  {/* Total summary */}
                  <hr style={{ margin: '8px 0', borderColor: '#e2e8f0' }} />
                  <Flex alignItems="center" justify="space-between">
                    <Text fontSize="sm" fontWeight="bold">
                      Total Revenue:
                    </Text>
                    <Text fontSize="sm" fontWeight="bold">
                      {formatCurrency(data.total, currency)}
                    </Text>
                  </Flex>
                </>
              );
            })()}
          </>
        ) : (
          // Show original total and previous data when no merchants
          <>
            <Flex alignItems="center" mb="1">
              <Box w="10px" h="10px" bg="#000000" borderRadius="50%" mr="2" />
              <Text fontSize="sm">
                {data.time}: {formatCurrency(data.total, currency)}
              </Text>
            </Flex>
            <Flex alignItems="center" mb="1">
              <Box w="10px" h="10px" bg="#D3D3D3" borderRadius="50%" mr="2" />
              <Text fontSize="sm">
                {data.prevTime}: {formatCurrency(data.prevTotal, currency)}
              </Text>
            </Flex>
          </>
        )}
      </div>
    );
  }
  return null;
};

CustomTooltipForGrossRevenue.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  currency: PropTypes.string,
  showAverage: PropTypes.bool,
  grossRevenueAvg: PropTypes.number,
  globalMerchantColors: PropTypes.object,
};

export const CustomTooltipForPowerbankRented = ({
  active,
  payload,
  powerbankShowAvg,
  grossRevenueCountAvg,
  globalMerchantColors,
}) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div
        className="custom-tooltip"
        style={{
          background: '#fff',
          padding: '10px',
          border: '1px solid #ccc',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          maxWidth: '300px',
        }}
      >
        {/* Average */}
        {powerbankShowAvg && (
          <Flex alignItems="center" mb="2">
            <Box w="10px" h="10px" bg="red" borderRadius="50%" mr="2" />
            <Text fontSize="sm">Average: {grossRevenueCountAvg ?? 0}</Text>
          </Flex>
        )}

        {/* Conditional content based on merchant data */}
        {data.merchants &&
        Array.isArray(data.merchants) &&
        data.merchants.length > 0 ? (
          // Show merchant details when merchants exist
          <>
            <Text fontWeight="bold" mb="2" fontSize="sm">
              {data.time}
            </Text>
            {(() => {
              // Filter out merchants with zero values and sort by total (descending)
              const activeMerchants = data.merchants
                .filter((merchant) => Number(merchant.total) > 0)
                .sort((a, b) => Number(b.total) - Number(a.total));

              const maxDisplay = 8; // Show max 8 merchants
              const displayMerchants = activeMerchants.slice(0, maxDisplay);
              const remainingCount = activeMerchants.length - maxDisplay;

              return (
                <>
                  {/* Scrollable container for merchant list */}
                  <Box maxH="200px" overflowY="auto" mb="2">
                    {displayMerchants.map((merchant) => {
                      const color =
                        globalMerchantColors[merchant.merchantId] || '#666';

                      return (
                        <Flex
                          key={merchant.merchantId}
                          alignItems="center"
                          mb="1"
                        >
                          <Box
                            w="8px"
                            h="8px"
                            bg={color}
                            borderRadius="50%"
                            mr="2"
                          />
                          <Text fontSize="xs" fontWeight="medium" flex="1">
                            {merchant.name.length > 15
                              ? `${merchant.name.substring(0, 15)}...`
                              : merchant.name}
                            :{' '}
                            <Text as="span" fontWeight="bold">
                              {Number(merchant.total)}
                            </Text>
                          </Text>
                        </Flex>
                      );
                    })}
                  </Box>

                  {/* Summary for remaining merchants */}
                  {remainingCount > 0 && (
                    <Text fontSize="xs" color="gray.500" textAlign="center">
                      +{remainingCount} more merchants
                    </Text>
                  )}

                  {/* Total summary */}
                  <hr style={{ margin: '8px 0', borderColor: '#e2e8f0' }} />
                  <Flex alignItems="center" justify="space-between">
                    <Text fontSize="sm" fontWeight="bold">
                      Total Powerbanks:
                    </Text>
                    <Text fontSize="sm" fontWeight="bold">
                      {data.total}
                    </Text>
                  </Flex>
                </>
              );
            })()}
          </>
        ) : (
          // Show original total and previous data when no merchants
          <>
            <Flex alignItems="center" mb="1">
              <Box w="10px" h="10px" bg="#000000" borderRadius="50%" mr="2" />
              <Text fontSize="sm">
                {data.time}: {data.total}
              </Text>
            </Flex>
            <Flex alignItems="center" mb="1">
              <Box w="10px" h="10px" bg="#D3D3D3" borderRadius="50%" mr="2" />
              <Text fontSize="sm">
                {data.prevTime}: {data.prevTotal}
              </Text>
            </Flex>
          </>
        )}
      </div>
    );
  }
  return null;
};

CustomTooltipForPowerbankRented.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  powerbankShowAvg: PropTypes.bool,
  grossRevenueCountAvg: PropTypes.number,
  globalMerchantColors: PropTypes.object,
};

export const CustomTooltipForRecurring = ({
  active,
  payload,
  adminDetails,
  sideBarRoutes,
}) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const label =
      adminDetails?.role !== sideBarRoutes?.Admin ? (
        <p>Station: {data.name}</p>
      ) : (
        <p>Merchant: {data.name}</p>
      );
    return (
      <div
        className="custom-tooltip"
        style={{
          background: '#fff',
          padding: '10px',
          border: '1px solid #ccc',
        }}
      >
        {label}
        <p>Count: {data.count}</p>
      </div>
    );
  }
  return null;
};

CustomTooltipForRecurring.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  adminDetails: PropTypes.object,
  sideBarRoutes: PropTypes.object,
};
